import { Container, Eyebrow, Heading, Reveal, Section, Text } from '@nexa/ui';
import aiGlow1200 from '@/assets/ai-glow-1200.webp';
import { useCopy } from '@/core/i18n';
import { AgentTypeCards } from './AgentTypeCards.tsx';
import { CapabilityCards } from './CapabilityCards.tsx';
import { SampleAgentCards } from './SampleAgentCards.tsx';
import { SkillsBanner } from './SkillsBanner.tsx';

/**
 * `#ai` — `template.html:895`.
 *
 * `id="ai"` là hợp đồng đóng băng: `Header.tsx` khai nó trong `SPY_IDS`, và `NAV_ALIASES` gộp
 * `knowledge` vào đây ⇒ mục nav "AI" sáng cho cả hai section. Sai một ký tự thì scroll-spy tắt
 * IM LẶNG và không test nào bắt (jsdom không có IntersectionObserver thật).
 *
 * Lead dùng `Text` với `tone="on-dark-74"` — mức trắng-alpha 74% của thiết kế (dòng 901).
 *
 * Bản đầu ráp tay bằng thẻ `p` trần vì `Text` khi đó chỉ phơi 78% và 60%, và ghi lý do đó ngay ở
 * đây. Lý do ĐÓ ĐÃ HẾT: `Text` giờ có đủ bốn mức lead trên nền tối mà thiết kế dùng
 * (`#cta` 80 · hero 78 · `#ai` 74 · `#security` 66). Giữ lại câu cũ thì nó thành một lời khai sai
 * — đúng lớp lỗi mà docblock của `MockRowList` đã mắc và làm lô này mất một vòng đo.
 *
 * Khối đầu vẫn KHÔNG dùng `SectionHead`, nhưng vì một lý do khác và nhỏ hơn: bề rộng khối chữ đang
 * nằm trên `Reveal`, còn `SectionHead` tự mang bề rộng của nó. Đổi sang `SectionHead` là dời chỗ
 * đặt `max-width` — một thay đổi bố cục CHƯA AI ĐO. Không phải không làm được; là chưa đo.
 */
export function AiAgentsSection() {
  const copy = useCopy();

  return (
    <Section
      id="ai"
      tone="gradient-ai"
      labelledBy="ai-heading"
      cv
      // Mồi content-visibility khai theo bậc trong `packages/ui/src/motion.css`, không ở đây.
      className="relative overflow-hidden nx-cv-ai"
    >
      {/*
        Ảnh trang trí, KHÔNG phải ứng viên LCP (section nằm dưới màn đầu) ⇒ `lazy`, khác hero.
        `alt=""` vì nó thuần trang trí. Mức mờ dùng `--opacity-ai-image` (dòng 896). Bản đầu của
        lô C mượn tên `--opacity-cta-image` vì trùng giá trị .22 — nay `#ai` có tên riêng nên đổi
        mờ của một section sau này không kéo theo section kia.
      */}
      <img
        src={aiGlow1200}
        alt=""
        loading="lazy"
        decoding="async"
        className="nx-float pointer-events-none absolute top-[-10%] right-[-6%] w-[52%] opacity-[var(--opacity-ai-image)] mix-blend-screen"
      />

      <Container className="relative">
        <Reveal className="max-w-[62ch]">
          <Eyebrow tone="on-gradient">{copy.aiAgents.eyebrow}</Eyebrow>
          <Heading level={2} size="h2" tone="on-dark" id="ai-heading" className="mt-[14px]">
            {copy.aiAgents.heading}
          </Heading>
          <Text size="lead" tone="on-dark-74" className="mt-4">
            {copy.aiAgents.lead}
          </Text>
        </Reveal>

        <AgentTypeCards types={copy.aiAgents.types} />
        <CapabilityCards capabilities={copy.aiAgents.capabilities} />
        <SampleAgentCards eyebrow={copy.aiAgents.samplesEyebrow} samples={copy.aiAgents.samples} />
        <SkillsBanner label={copy.aiAgents.skillsLabel} body={copy.aiAgents.skillsBody} />
      </Container>
    </Section>
  );
}
